import { ColumnTable, DisplayInfo, Table, Button } from '@octano/global-ui';
import { useTranslation } from 'react-i18next';
import { format } from 'rut.js';

import { useMemo } from 'react';
import { InterimDegreeAuthorizedStudentSingle } from '../interfaces/interim-degree-authorized-student-single.interface';

const dictPrefix = 'interimDegreeGraduationProcess.single';

interface Props {
  data: InterimDegreeAuthorizedStudentSingle[];
  isLoadingResults: boolean;
  hasFilters: boolean;
  itemPerPage: number;
  totalItems: number;
  totalPages: number;
  currentPage: number;
  onChangePage: (page: number) => void;
  onPressGenerate?: (
    studyPlanEnrollmentId: number,
    interimDegreeId: number,
  ) => void;
}

export function InterimDegreeSingleStudentTable({
  data,
  isLoadingResults,
  hasFilters,
  itemPerPage,
  totalPages,
  totalItems,
  currentPage,
  onChangePage,
  onPressGenerate,
}: Props) {
  const { t } = useTranslation();

  const columns = useMemo(() => {
    const cols: ColumnTable<InterimDegreeAuthorizedStudentSingle>[] = [
      {
        headerText: t(`${dictPrefix}.table.name`),
        columnName: 'name',
        tdClassName: 'text-center',
        thClassName: 'text-center',
        cellFormat: ({ value }) => (
          <span className="text-uppercase">{value}</span>
        ),
      },
      {
        headerText: t(`${dictPrefix}.table.identification`),
        columnName: 'identification',
        tdClassName: 'text-center',
        thClassName: 'text-center',
        cellFormat: ({ value }) => format(value),
      },
      {
        headerText: t(`${dictPrefix}.table.studyPlan`),
        columnName: 'studyPlan',
        tdClassName: 'text-center',
        thClassName: 'text-center',
        cellFormat: ({ value }) => (
          <span className="text-uppercase">{value}</span>
        ),
      },
      {
        headerText: t(`${dictPrefix}.table.interimDegree`),
        columnName: 'interimDegree',
        tdClassName: 'text-center',
        thClassName: 'text-center',
        cellFormat: ({ value }) => (
          <span className="text-uppercase">{value}</span>
        ),
      },
      {
        headerText: t(`${dictPrefix}.table.action`),
        columnName: 'id',
        tdClassName: 'text-center',
        thClassName: 'text-center',
        width: 180,
        cellFormat: ({ row }) => (
          <>
            <Button
              text={t(`${dictPrefix}.actions.generateAuthorizations`)}
              type="button"
              size="sm"
              disabled={!!row.status}
              onClick={() =>
                !!onPressGenerate &&
                onPressGenerate(row.studyPlanEnrollmentId, row.interimDegreeId)
              }
              outlined
            />
          </>
        ),
      },
    ];
    return cols;
  }, [t, onPressGenerate]);

  return (
    <div className="w-100">
      <Table
        isLoadingResults={isLoadingResults}
        data={data}
        columns={columns}
        pagination={{
          currentPage: currentPage + 1,
          itemsPerPage: itemPerPage,
          totalItems: totalItems,
          totalPages: totalPages,
          onChangePage: (page) => onChangePage(page - 1),
        }}
        noResultsText={
          <div className="d-flex justify-content-center align-items-center py-5">
            <DisplayInfo
              iconName="information"
              textBody={t(
                `${dictPrefix}.${
                  hasFilters ? 'noResultsFilter' : 'noResults'
                }.description`,
              )}
              maxWidth="650px"
            />
          </div>
        }
      />
    </div>
  );
}
